/**
 * Notification Service
 *
 * Handles all notification-related operations via API Gateway REST API.
 * Provides methods to list, mark as read, mark all as read, delete,
 * and count unread notifications for the authenticated user.
 *
 * Follows the same pattern as JobService:
 * - Constructor validates environment, stores API base URL
 * - Private apiRequest() adds Cognito JWT to Authorization header
 * - Public methods call apiRequest() and return typed results
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7
 */

import { AuthenticatedApiClient } from './authenticated-api-client';
import type { Notification } from '../types';

/**
 * Notification Service class for managing user notifications
 */
export class NotificationService {
    private client: AuthenticatedApiClient;

    /**
     * Creates a new NotificationService instance
     *
     * @throws Error if VITE_API_GATEWAY_URL is not set
     */
    constructor() {
        this.validateEnvironment();
        this.client = new AuthenticatedApiClient();
    }

    /**
     * Validates that the API Gateway URL environment variable is set
     * @throws Error if VITE_API_GATEWAY_URL is missing
     */
    private validateEnvironment(): void {
        if (!import.meta.env.VITE_API_GATEWAY_URL) {
            throw new Error(
                'VITE_API_GATEWAY_URL environment variable is not set'
            );
        }
    }

    /**
     * Lists notifications for the authenticated user.
     * Fetches all pages by following next_cursor until exhausted.
     *
     * @returns Promise resolving to array of notifications
     */
    async listNotifications(): Promise<Notification[]> {
        const all: Notification[] = [];
        let cursor: string | undefined;

        do {
            const params = new URLSearchParams();
            params.append('limit', '50');
            if (cursor) params.append('cursor', cursor);

            const endpoint = `/notifications?${params.toString()}`;
            const response = await this.client.apiRequest(endpoint, { method: 'GET' });
            const data = await response.json();

            if (Array.isArray(data)) {
                all.push(...data);
                break;
            }

            all.push(...(data.notifications ?? []));
            cursor = data.next_cursor;
        } while (cursor);

        return all;
    }

    /**
     * Marks a single notification as read.
     *
     * @param notificationId - The notification ID to mark as read
     */
    async markAsRead(notificationId: string): Promise<void> {
        await this.client.apiRequest(`/notifications/${notificationId}`, { method: 'PATCH' });
    }

    /**
     * Marks all notifications for the authenticated user as read.
     */
    async markAllAsRead(): Promise<void> {
        await this.client.apiRequest('/notifications/read-all', { method: 'PATCH' });
    }

    /**
     * Deletes a notification.
     *
     * @param notificationId - The notification ID to delete
     */
    async deleteNotification(notificationId: string): Promise<void> {
        await this.client.apiRequest(`/notifications/${notificationId}`, { method: 'DELETE' });
    }

    /**
     * Returns the count of unread notifications.
     * Tries the dedicated count endpoint first; falls back to list+filter
     * if the endpoint returns 404 (not yet deployed).
     *
     * @returns Promise resolving to the number of unread notifications
     */
    async getUnreadCount(): Promise<number> {
        try {
            const response = await this.client.apiRequest('/notifications/unread-count', { method: 'GET' });
            const data = await response.json();
            return typeof data.count === 'number' ? data.count : data.count ?? 0;
        } catch (error) {
            const is404 = error instanceof Error && error.message.includes('404');
            if (is404) {
                const notifications = await this.listNotifications();
                return notifications.filter(n => !n.read).length;
            }
            throw error;
        }
    }
}
